/**
 * WEB COPY of DocumentsDataScreen.tsx — picked by the web bundler ahead of
 * the mobile file (`.web.tsx` resolves first); Metro never sees it.
 *
 * One document (web app Phase 5). A stored file is decrypted in the browser
 * and previewed inline (PDF, images, text), downloaded under its name, or
 * opened in a new tab; its name, category, type and tags are edited in
 * place; a new file can replace it. A paper-copy record shows where it is
 * kept and edits on the form screen. Either kind can be deleted, which
 * removes it from the phone too.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';

import backIcon from '../../../assets/icons/back.png';
import { shouldRouteToUnlock } from '../../../navigation/postAuthUnlockRoute';
import { vaultService } from '../../../services/vaultService';
import {
  CloudDocument,
  cloudDocuments,
  documentTypeFor,
  effectiveMimeType,
} from '../../../services/documents/cloudDocuments.web';
import { createLogger } from '../../../utils/logger';
import { ms, s, vs } from '../../../utils/responsive';
import {
  browser,
  CATEGORIES,
  DOCUMENT_TYPES,
  TAGS,
  categoryId,
  categoryName,
  chooseFiles,
  downloadName,
  errorMessage,
  formatBytes,
  formatDate,
  saveBlob,
  withVaultKey,
} from './documentsShared.web';

const logger = createLogger('DocumentsData.web');

type Physical = { id: string; documentName: string; category: string; documentType: string; location: string; purpose?: string };

const PREVIEWABLE = /^(application\/pdf|image\/(png|jpe?g|gif|webp)|text\/plain)$/i;

/**
 * The type a decrypted file may be rendered as inside the site, or null
 * when it may only be downloaded. Never HTML, SVG or XML: those can carry
 * script, and a blob: page runs in this site's origin (pen test, 2026-10-02).
 */
const safeInlineType = (mime: string): string | null => {
  const m = (mime || '').toLowerCase().split(';')[0].trim();
  if (!PREVIEWABLE.test(m)) return null;
  return m === 'image/jpg' ? 'image/jpeg' : m;
};

export const DocumentsDataScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const documentId: string = route.params?.documentId;
  // K5 / Step 5.6: another person's document, read-only, opened with the
  // key they left for this successor.
  const targetUserId: string | undefined = route.params?.targetUserId;
  const viewOnly = !!targetUserId && !!route.params?.viewOnly;
  const returnTo = {
    root: 'Documents',
    routes: [
      { name: 'DocumentsDashboardScreen', params: { targetUserId, viewOnly } },
      { name: 'DocumentsDataScreen', params: { documentId, targetUserId, viewOnly } },
    ],
  };

  const [doc, setDoc] = useState<CloudDocument | null>(null);
  const [paper, setPaper] = useState<Physical | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [needsKey, setNeedsKey] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ fileName: '', category: 'other', documentType: 'Other', tags: [] as string[] });
  const previewRef = useRef<string | null>(null);

  const setPreview = (b: Blob | null) => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    // Shown in the page only as one of the safe types, and typed as that
    // type whatever the stored metadata says.
    const safe = b && doc ? safeInlineType(effectiveMimeType(doc)) : null;
    const url = b && safe ? URL.createObjectURL(new Blob([b], { type: safe, lastModified: Date.now() })) : null;
    previewRef.current = url;
    setPreviewUrl(url);
    setBlob(b);
  };

  useEffect(() => () => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await cloudDocuments.list(targetUserId);
      const found = list.files.find((f) => f.documentId === documentId) ?? null;
      if (found) {
        setDoc(found);
        setForm({
          fileName: found.fileName,
          category: categoryId(found.category),
          documentType: found.documentType || 'Other',
          tags: found.tags ?? [],
        });
        setNotFound(false);
        return;
      }
      const meta = viewOnly ? null : await vaultService.getDocumentMetadata(documentId).catch(() => null);
      if (meta) {
        setPaper({ id: documentId, ...(meta as any) });
        setNotFound(false);
      } else {
        setNotFound(true);
      }
    } catch (error) {
      logger.error('Could not load the document:', error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const openFile = useCallback(async () => {
    if (!doc) return null;
    setOpening(true);
    try {
      const out: { blob: Blob | null } = { blob: null };
      const ran = await withVaultKey(returnTo, async () => {
        out.blob = (await cloudDocuments.download(doc.documentId, targetUserId)).blob;
      });
      setNeedsKey(!ran);
      if (out.blob) setPreview(out.blob);
      return out.blob;
    } catch (error) {
      logger.error('Could not open the file:', error);
      Alert.alert("Couldn't open the file", errorMessage(error, 'Try again.'));
      return null;
    } finally {
      setOpening(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc]);

  // Preview straight away when this browser already holds the key.
  useEffect(() => {
    if (!doc || blob) return;
    shouldRouteToUnlock().then((locked) => {
      if (locked) setNeedsKey(true);
      else if (PREVIEWABLE.test(effectiveMimeType(doc))) openFile();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc]);

  const download = async () => {
    const b = blob ?? (await openFile());
    if (b && doc) saveBlob(b, downloadName(doc.fileName, effectiveMimeType(doc)));
  };

  // A new tab renders the file inside this site's origin, so only the
  // safe types open there; anything else is offered as a download.
  const openInTab = async () => {
    const b = blob ?? (await openFile());
    if (!b || !doc) return;
    const safe = safeInlineType(effectiveMimeType(doc));
    if (!safe) {
      saveBlob(b, downloadName(doc.fileName, effectiveMimeType(doc)));
      return;
    }
    const url = URL.createObjectURL(new Blob([b], { type: safe, lastModified: Date.now() }));
    browser.window.open(url, '_blank', 'noopener');
    // The tab has the file once it has loaded; the address should not
    // stay usable after that.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const saveEdits = async () => {
    if (!doc) return;
    if (!form.fileName.trim()) {
      Alert.alert('The document needs a name');
      return;
    }
    setBusy(true);
    try {
      const updated = await cloudDocuments.update(doc.documentId, {
        fileName: form.fileName.trim(),
        category: form.category,
        documentType: form.documentType,
        tags: form.tags,
      });
      setDoc(updated);
      setEditing(false);
    } catch (error) {
      Alert.alert('Could not save the changes', errorMessage(error, 'Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const replaceFile = async () => {
    if (!doc) return;
    const [file] = await chooseFiles(false);
    if (!file) return;
    setBusy(true);
    try {
      await withVaultKey(returnTo, async () => {
        const updated = await cloudDocuments.upload(
          file,
          {
            fileName: doc.fileName,
            category: categoryId(doc.category),
            documentType: documentTypeFor(file.name, file.type),
            tags: doc.tags ?? [],
          },
          doc.documentId,
        );
        setDoc(updated);
        setPreview(null);
      });
    } catch (error) {
      Alert.alert('Could not replace the file', errorMessage(error, 'Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = () => {
    Alert.alert('Delete this document?', 'It is removed here and from your phone. This cannot be undone.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          setBusy(true);
          try {
            if (doc) await cloudDocuments.remove(doc.documentId);
            else if (paper) await vaultService.deleteDocumentMetadata(paper.id);
            navigation.navigate('DocumentsDashboardScreen', { targetUserId, viewOnly });
          } catch (error) {
            Alert.alert('Could not delete the document', errorMessage(error, 'Try again.'));
          } finally {
            setBusy(false);
          }
        },
      },
    ]);
  };

  const mime = doc ? effectiveMimeType(doc) : '';

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.navigate('DocumentsDashboardScreen', { targetUserId, viewOnly })}
          accessibilityRole="button"
          accessibilityLabel="Back to documents"
        >
          <Image source={backIcon} style={styles.backIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle} numberOfLines={1}>
          {doc?.fileName ?? paper?.documentName ?? 'Document'}
        </Text>
        <View style={styles.backButton} />
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.column}>
          {loading ? (
            <View style={styles.card}>
              <ActivityIndicator color="#29BFC0" />
            </View>
          ) : notFound ? (
            <View style={styles.card}>
              <Text style={styles.title}>This document is no longer here</Text>
              <Text style={styles.muted}>It may have been deleted on your phone or in another window.</Text>
              <TouchableOpacity onPress={() => navigation.navigate('DocumentsDashboardScreen', { targetUserId, viewOnly })}>
                <Text style={styles.link}>Back to documents</Text>
              </TouchableOpacity>
            </View>
          ) : doc ? (
            <>
              <View style={styles.card}>
                {editing ? (
                  <View style={{ gap: 14 }}>
                    <View>
                      <Text style={styles.label}>Name</Text>
                      <TextInput
                        style={styles.input}
                        value={form.fileName}
                        onChangeText={(t) => setForm({ ...form, fileName: t })}
                        maxLength={255}
                      />
                    </View>
                    <ChipGroup
                      label="Category"
                      options={CATEGORIES.map((c) => ({ value: c.id, label: c.name }))}
                      value={form.category}
                      onChange={(v) => setForm({ ...form, category: v })}
                    />
                    <ChipGroup
                      label="Type"
                      options={DOCUMENT_TYPES.map((t) => ({ value: t, label: t }))}
                      value={form.documentType}
                      onChange={(v) => setForm({ ...form, documentType: v })}
                    />
                    <ChipGroup
                      label="Tags"
                      options={TAGS.map((t) => ({ value: t, label: t }))}
                      values={form.tags}
                      onToggle={(v) =>
                        setForm({
                          ...form,
                          tags: form.tags.includes(v) ? form.tags.filter((t) => t !== v) : [...form.tags, v],
                        })
                      }
                    />
                    <View style={styles.actions}>
                      <TouchableOpacity style={styles.primaryButton} onPress={saveEdits} disabled={busy}>
                        <Text style={styles.primaryButtonText}>{busy ? 'Saving…' : 'Save changes'}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.secondaryButton} onPress={() => setEditing(false)} disabled={busy}>
                        <Text style={styles.secondaryButtonText}>Cancel</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ) : (
                  <>
                    <Text style={styles.title}>{doc.fileName}</Text>
                    <Text style={styles.muted}>
                      {[categoryName(doc.category), doc.documentType, formatBytes(doc.fileSizeBytes), `Added ${formatDate(doc.createdAt)}`]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                    {(doc.tags ?? []).length > 0 && (
                      <View style={styles.tagRow}>
                        {doc.tags.map((t) => (
                          <View key={t} style={styles.tag}>
                            <Text style={styles.tagText}>{t}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                    <View style={styles.actions}>
                      <TouchableOpacity style={styles.primaryButton} onPress={download} disabled={opening || busy}>
                        <Text style={styles.primaryButtonText}>Download</Text>
                      </TouchableOpacity>
                      {PREVIEWABLE.test(mime) && (
                        <TouchableOpacity style={styles.secondaryButton} onPress={openInTab} disabled={opening || busy}>
                          <Text style={styles.secondaryButtonText}>Open in a new tab</Text>
                        </TouchableOpacity>
                      )}
                      {!viewOnly && (
                        <>
                          <TouchableOpacity style={styles.secondaryButton} onPress={() => setEditing(true)} disabled={busy}>
                            <Text style={styles.secondaryButtonText}>Edit details</Text>
                          </TouchableOpacity>
                          <TouchableOpacity style={styles.secondaryButton} onPress={replaceFile} disabled={busy}>
                            <Text style={styles.secondaryButtonText}>{busy ? 'Working…' : 'Replace file'}</Text>
                          </TouchableOpacity>
                          <TouchableOpacity style={styles.dangerButton} onPress={remove} disabled={busy}>
                            <Text style={styles.dangerButtonText}>Delete</Text>
                          </TouchableOpacity>
                        </>
                      )}
                    </View>
                  </>
                )}
              </View>

              <View style={styles.previewCard}>
                {opening ? (
                  <View style={styles.previewEmpty}>
                    <ActivityIndicator color="#29BFC0" />
                    <Text style={styles.muted}>Decrypting…</Text>
                  </View>
                ) : previewUrl && PREVIEWABLE.test(mime) ? (
                  mime.startsWith('image/') ? (
                    <img src={previewUrl} alt={doc.fileName} style={{ maxWidth: '100%', display: 'block', margin: '0 auto', borderRadius: 8 }} />
                  ) : (
                    <iframe
                      src={previewUrl}
                      title={doc.fileName}
                      // Plain text needs nothing, so it gets no permissions at
                      // all. The browser's PDF viewer does not run inside a
                      // sandboxed frame, so a PDF relies on its fixed type.
                      {...(mime.toLowerCase().startsWith('text/plain') ? { sandbox: '' } : {})}
                      style={{ width: '100%', height: '75vh', border: 0, borderRadius: 8, background: '#FFFFFF' }}
                    />
                  )
                ) : needsKey ? (
                  <View style={styles.previewEmpty}>
                    <Text style={styles.muted}>Your recovery code is needed to open this document in this browser.</Text>
                    <TouchableOpacity style={[styles.primaryButton, styles.centered]} onPress={openFile}>
                      <Text style={styles.primaryButtonText}>Unlock and preview</Text>
                    </TouchableOpacity>
                  </View>
                ) : PREVIEWABLE.test(mime) ? (
                  <View style={styles.previewEmpty}>
                    <TouchableOpacity style={[styles.primaryButton, styles.centered]} onPress={openFile}>
                      <Text style={styles.primaryButtonText}>Show preview</Text>
                    </TouchableOpacity>
                  </View>
                ) : (
                  <View style={styles.previewEmpty}>
                    <Text style={styles.muted}>
                      There is no preview for this kind of file in the browser. Download it to open it.
                    </Text>
                  </View>
                )}
              </View>
            </>
          ) : paper ? (
            <View style={styles.card}>
              <Text style={styles.title}>{paper.documentName}</Text>
              <Text style={styles.muted}>{[categoryName(paper.category), paper.documentType].filter(Boolean).join(' · ')}</Text>
              <View style={styles.field}>
                <Text style={styles.label}>Where the paper copy is kept</Text>
                <Text style={styles.value}>{paper.location || 'Not set'}</Text>
              </View>
              {paper.purpose ? (
                <View style={styles.field}>
                  <Text style={styles.label}>Notes</Text>
                  <Text style={styles.value}>{paper.purpose}</Text>
                </View>
              ) : null}
              <View style={styles.actions}>
                <TouchableOpacity
                  style={styles.primaryButton}
                  onPress={() =>
                    navigation.navigate('DocumentsInputScreen', {
                      documentId: paper.id,
                      existingDocument: { ...paper, hasPhysicalCopy: true, hasDigitalCopy: false, notes: paper.purpose },
                    })
                  }
                >
                  <Text style={styles.primaryButtonText}>Edit</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.dangerButton} onPress={remove} disabled={busy}>
                  <Text style={styles.dangerButtonText}>Delete</Text>
                </TouchableOpacity>
              </View>
            </View>
          ) : null}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

/** A row of choice chips: one value (`value`/`onChange`) or several (`values`/`onToggle`). */
export const ChipGroup: React.FC<{
  label: string;
  options: { value: string; label: string }[];
  value?: string;
  values?: string[];
  onChange?: (v: string) => void;
  onToggle?: (v: string) => void;
}> = ({ label, options, value, values, onChange, onToggle }) => (
  <View>
    <Text style={styles.label}>{label}</Text>
    <View style={styles.chips}>
      {options.map((o) => {
        const on = values ? values.includes(o.value) : value === o.value;
        return (
          <TouchableOpacity
            key={o.value}
            style={[styles.chip, on && styles.chipActive]}
            onPress={() => (values ? onToggle?.(o.value) : onChange?.(o.value))}
            accessibilityRole={values ? 'checkbox' : 'radio'}
            accessibilityState={values ? { checked: on } : { selected: on }}
          >
            <Text style={[styles.chipText, on && styles.chipTextActive]}>{o.label}</Text>
          </TouchableOpacity>
        );
      })}
    </View>
  </View>
);

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#F3F6F8' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: s(16),
    paddingVertical: vs(12),
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
  },
  backButton: { padding: s(4), width: ms(32) },
  backIcon: { width: ms(24), height: ms(24), resizeMode: 'contain' },
  headerTitle: { flex: 1, textAlign: 'center', fontSize: ms(16), fontWeight: '600', color: '#1A1A1A' },
  content: { paddingHorizontal: 16, paddingTop: 20, paddingBottom: 120 },
  column: { width: '100%', maxWidth: 900, alignSelf: 'center' },
  card: { backgroundColor: '#FFFFFF', borderRadius: 16, padding: 20, marginBottom: 16, gap: 6 },
  title: { fontSize: 20, fontWeight: '700', color: '#1C2C4F' },
  muted: { fontSize: 14, lineHeight: 20, color: '#4B5563', textAlign: 'left' },
  link: { marginTop: 8, fontSize: 14, color: '#0F8B8D', fontWeight: '600' },
  label: { fontSize: 13, fontWeight: '600', color: '#6B7280', marginBottom: 6 },
  value: { fontSize: 15, color: '#1A1A1A' },
  field: { marginTop: 14 },
  input: { borderWidth: 1, borderColor: '#DDE3EA', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, fontSize: 15, color: '#1A1A1A' },
  tagRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  tag: { borderRadius: 12, paddingVertical: 3, paddingHorizontal: 10, backgroundColor: '#EAF7F7' },
  tagText: { fontSize: 12, color: '#0F8B8D', fontWeight: '600' },
  actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 16 },
  primaryButton: { backgroundColor: '#29BFC0', borderRadius: 24, paddingVertical: 11, paddingHorizontal: 20, alignSelf: 'flex-start' },
  primaryButtonText: { color: '#FFFFFF', fontWeight: '700', fontSize: 15 },
  secondaryButton: { borderRadius: 24, paddingVertical: 11, paddingHorizontal: 20, borderWidth: 1.5, borderColor: '#1C2C4F' },
  secondaryButtonText: { color: '#1C2C4F', fontWeight: '600', fontSize: 15 },
  dangerButton: { borderRadius: 24, paddingVertical: 11, paddingHorizontal: 20, borderWidth: 1.5, borderColor: '#C2410C' },
  dangerButtonText: { color: '#C2410C', fontWeight: '600', fontSize: 15 },
  previewCard: { backgroundColor: '#FFFFFF', borderRadius: 16, padding: 12, minHeight: 200 },
  previewEmpty: { paddingVertical: 48, paddingHorizontal: 16, alignItems: 'center', gap: 14 },
  centered: { alignSelf: 'center' },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: { borderRadius: 18, paddingVertical: 7, paddingHorizontal: 14, backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#DDE3EA' },
  chipActive: { backgroundColor: '#1C2C4F', borderColor: '#1C2C4F' },
  chipText: { fontSize: 14, color: '#1C2C4F' },
  chipTextActive: { color: '#FFFFFF', fontWeight: '600' },
});

export default DocumentsDataScreen;
